import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import SEO from '../components/common/SEO';
import api from '../lib/api';
import { Image, Play, X } from 'lucide-react';

const Gallery = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      const res = await api.get('/site-media/files');
      setMedia(res.data?.files || []);
    } catch (error) {
      console.error('Failed to fetch media:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredMedia = filter === 'all' 
    ? media 
    : media.filter(item => item.type === filter);

  return (
    <div className="min-h-screen bg-[#050505]">
      <SEO page="gallery" />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#7000FF]/10 to-transparent" />
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              <span className="text-[#7000FF]">Gallery</span>
            </h1>
            <p className="text-lg md:text-xl text-white/70 max-w-3xl mx-auto">
              Explore moments from our performances, classes, and events. See our talented students shine on stage.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter Buttons */}
      <section className="py-6 px-4 border-b border-white/10">
        <div className="max-w-6xl mx-auto flex justify-center gap-4 flex-wrap">
          {['all', 'image', 'video'].map((type) => (
            <button
              key={type}
              onClick={() => setFilter(type)}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                filter === type
                  ? 'bg-[#FF007F] text-white'
                  : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
            >
              {type === 'all' ? 'All' : type === 'image' ? 'Photos' : 'Videos'}
            </button>
          ))}
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="aspect-square bg-white/5 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : filteredMedia.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredMedia.map((item, index) => (
                <motion.div
                  key={item.id || index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className="aspect-square relative group cursor-pointer overflow-hidden rounded-lg"
                  onClick={() => item.type === 'image' && setSelectedImage(item)}
                >
                  {item.type === 'video' ? (
                    <div className="w-full h-full bg-black flex items-center justify-center">
                      <video 
                        src={item.url} 
                        className="w-full h-full object-cover"
                        muted
                      />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <Play className="w-12 h-12 text-white" fill="white" />
                      </div>
                    </div>
                  ) : (
                    <img 
                      src={item.url || item.path} 
                      alt={item.name || 'Gallery image'}
                      className="w-full h-full object-cover transition-transform group-hover:scale-110"
                      loading="lazy"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                    <span className="text-white text-sm truncate">{item.name || 'UPA Photo'}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <Image className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Gallery Coming Soon</h3>
              <p className="text-white/60">
                Photos and videos from our performances will be added here soon.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-full"
            onClick={() => setSelectedImage(null)}
          >
            <X className="w-8 h-8" />
          </button>
          <img 
            src={selectedImage.url || selectedImage.path} 
            alt={selectedImage.name || 'Gallery image'}
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </motion.div>
      )}

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-[#FF007F]/10 to-[#7000FF]/10 text-center">
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
          Want to be part of the action?
        </h2>
        <p className="text-white/60 mb-6 max-w-2xl mx-auto">
          Join UPA and perform on stage with us. Book your free trial today!
        </p>
        <a 
          href="/free-trial" 
          className="inline-flex items-center px-8 py-3 bg-[#FF007F] text-white font-bold rounded-lg hover:bg-[#D6006A] transition-colors"
        >
          Book Free Trial
        </a>
      </section>
    </div>
  );
};

export default Gallery;
